/**
 * SoundManager — Thin facade over MusicManager and SFXSynthesizer.
 * Singleton: call SoundManager.init() once, then SoundManager.play('shoot'), etc.
 *
 * Events listened (via EventBus):
 *   robotFired, robotExploded, enemyDestroyed, enemyReachedBase,
 *   waveStarted, waveCompleted, robotPlaced, baseDamaged, gameOver
 *
 * Implementation split:
 *   - MusicManager  → music loading, playback, crossfade, volume
 *   - SFXSynthesizer → Web Audio synthesis, MP3 overrides, engine loops
 */

import { EventBus, GameEvents } from '@/events/EventBus';
import { MusicManager, MusicTrack, MusicTrackKey } from '@/systems/sound/MusicManager';
import { SFXSynthesizer, SfxKey, MECHANICAL_ENEMY_TYPES, EngineSoundEntity } from '@/systems/sound/SFXSynthesizer';

// Re-export public types so existing imports don't break
export { MusicTrack } from '@/systems/sound/MusicManager';
export type { MusicTrackKey } from '@/systems/sound/MusicManager';
export type { SfxKey, EngineSoundEntity } from '@/systems/sound/SFXSynthesizer';
export { MECHANICAL_ENEMY_TYPES } from '@/systems/sound/SFXSynthesizer';

class SoundManagerClass {
  private _fxVolume: number = 0.5;
  private _musicVolume: number = 0.3;
  private _muted: boolean = false;

  private initialized = false;
  private eventsConnected = false;

  private music: MusicManager;
  private sfx: SFXSynthesizer;

  constructor() {
    this.music = new MusicManager(
      () => this._musicVolume,
      () => this._muted,
    );
    this.sfx = new SFXSynthesizer(
      () => this._fxVolume,
      () => this._muted,
    );
  }

  // ── Public API ──

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.music.loadMusicTracks();
    this.sfx.loadSfxOverrides();
    this.connectEvents();
  }

  // ── Volume / Mute ──

  get fxVolume(): number { return this._fxVolume; }
  set fxVolume(v: number) {
    this._fxVolume = Math.max(0, Math.min(1, v));
    this.sfx.syncEngineGain();
  }

  get musicVolume(): number { return this._musicVolume; }
  set musicVolume(v: number) {
    this._musicVolume = Math.max(0, Math.min(1, v));
    this.music.applyVolume();
  }

  get muted(): boolean { return this._muted; }
  set muted(m: boolean) {
    this._muted = m;
    this.music.applyVolume();
    this.sfx.syncEngineGain();
  }

  // ── Per-SFX volume ──

  getSfxVolume(key: SfxKey): number { return this.sfx.getSfxVolume(key); }
  setSfxVolume(key: SfxKey, v: number): void { this.sfx.setSfxVolume(key, v); }
  exportSfxVolumes(): Record<string, number> { return this.sfx.exportSfxVolumes(); }
  importSfxVolumes(data: Record<string, number>): void { this.sfx.importSfxVolumes(data); }

  // ── SFX playback ──

  play(key: SfxKey): void { this.sfx.play(key); }
  playAt(key: SfxKey, worldX: number, worldY: number, camX: number, camY: number): void {
    this.sfx.playAt(key, worldX, worldY, camX, camY);
  }

  // ── Music playback ──

  playMusic(key: MusicTrackKey): void { this.music.playMusic(key); }
  stopMusic(): void { this.music.stopMusic(); }
  playRandomLevelMusic(): void { this.music.playRandomLevelMusic(); }
  playLevelMusicForMap(mapIndex: number): void { this.music.playLevelMusicForMap(mapIndex); }
  playRandomMapSelectMusic(): void { this.music.playRandomMapSelectMusic(); }
  getCurrentMusicKey(): string { return this.music.getCurrentMusicKey(); }
  getMusicProgress(): { progress: number; currentTime: number; duration: number } {
    return this.music.getMusicProgress();
  }
  seekMusic(progress: number): void { this.music.seekMusic(progress); }

  // ── Engine loops ──

  startEngineLoops(): void { this.sfx.startEngineLoops(); }
  stopEngineLoops(): void { this.sfx.stopEngineLoops(); }
  updateEngineLoops(entities: EngineSoundEntity[], camX: number, camY: number): void {
    this.sfx.updateEngineLoops(entities, camX, camY);
  }

  // ── Event bus integration ──

  private _onRobotFired = (data: { weaponType: string }) => {
    const t = data.weaponType;
    if (t.includes('MISSILE') || t.includes('missile')) {
      this.play('missile');
    } else if (t.includes('BURST') || t.includes('burst')) {
      this.play('burst');
    } else if (t.includes('HEAVY') || t.includes('heavy')) {
      this.play('shootHeavy');
    } else {
      this.play('shoot');
    }
  };
  private _onRobotExploded = () => this.play('robotExplode');
  private _onEnemyDestroyed = () => {
    this.play('enemyDeath');
    if (Math.random() < 0.25) this.play('enemyDeathGrunt');
  };
  private _onEnemyReachedBase = () => this.play('baseHit');
  private _onWaveStarted = () => this.play('waveStart');
  private _onWaveCelebration = () => this.play('waveComplete');
  private _onRobotPlaced = () => this.play('robotPlace');
  private _onBaseDamaged = () => this.play('baseHit');
  private _onMissileExploded = () => this.play('missileExplosion');
  private _onProjectileHit = () => this.play('hit');
  private _onGameOver = (won: boolean) => this.play(won ? 'victory' : 'defeat');
  private _onBaseDestroyed = () => this.play('baseDestroyed');
  private _lastSpawnSfx = 0;
  private _onEnemySpawned = () => {
    const now = Date.now();
    if (now - this._lastSpawnSfx > 2000) { this._lastSpawnSfx = now; this.play('enemySpawn'); }
  };
  private _onRobotDestroyed = () => this.play('robotDeath');
  private _onPhaseChanged = () => this.play('phaseChange');
  private _onLevelUnlocked = () => this.play('levelUnlock');
  private _onComponentUnlocked = () => this.play('componentUnlock');
  private _onGridExpanded = () => this.play('gridExpand');

  private connectEvents(): void {
    if (this.eventsConnected) return;
    this.eventsConnected = true;

    EventBus.on(GameEvents.ROBOT_FIRED, this._onRobotFired, this);
    EventBus.on(GameEvents.ROBOT_EXPLODED, this._onRobotExploded, this);
    EventBus.on(GameEvents.ENEMY_DESTROYED, this._onEnemyDestroyed, this);
    EventBus.on(GameEvents.ENEMY_REACHED_BASE, this._onEnemyReachedBase, this);
    EventBus.on(GameEvents.WAVE_STARTED, this._onWaveStarted, this);
    EventBus.on(GameEvents.WAVE_CELEBRATION, this._onWaveCelebration, this);
    EventBus.on(GameEvents.ROBOT_PLACED, this._onRobotPlaced, this);
    EventBus.on(GameEvents.BASE_DAMAGED, this._onBaseDamaged, this);
    EventBus.on(GameEvents.MISSILE_EXPLODED, this._onMissileExploded, this);
    EventBus.on(GameEvents.PROJECTILE_HIT, this._onProjectileHit, this);
    EventBus.on(GameEvents.GAME_OVER, this._onGameOver, this);
    EventBus.on(GameEvents.BASE_DESTROYED, this._onBaseDestroyed, this);
    EventBus.on(GameEvents.ENEMY_SPAWNED, this._onEnemySpawned, this);
    EventBus.on(GameEvents.ROBOT_DESTROYED, this._onRobotDestroyed, this);
    EventBus.on(GameEvents.PHASE_CHANGED, this._onPhaseChanged, this);
    EventBus.on(GameEvents.LEVEL_UNLOCKED, this._onLevelUnlocked, this);
    EventBus.on(GameEvents.COMPONENT_UNLOCKED, this._onComponentUnlocked, this);
    EventBus.on(GameEvents.GRID_EXPANDED, this._onGridExpanded, this);
  }

  /** Disconnect all EventBus listeners (for proper cleanup) */
  disconnectEvents(): void {
    if (!this.eventsConnected) return;
    this.eventsConnected = false;

    EventBus.off(GameEvents.ROBOT_FIRED, this._onRobotFired, this);
    EventBus.off(GameEvents.ROBOT_EXPLODED, this._onRobotExploded, this);
    EventBus.off(GameEvents.ENEMY_DESTROYED, this._onEnemyDestroyed, this);
    EventBus.off(GameEvents.ENEMY_REACHED_BASE, this._onEnemyReachedBase, this);
    EventBus.off(GameEvents.WAVE_STARTED, this._onWaveStarted, this);
    EventBus.off(GameEvents.WAVE_CELEBRATION, this._onWaveCelebration, this);
    EventBus.off(GameEvents.ROBOT_PLACED, this._onRobotPlaced, this);
    EventBus.off(GameEvents.BASE_DAMAGED, this._onBaseDamaged, this);
    EventBus.off(GameEvents.MISSILE_EXPLODED, this._onMissileExploded, this);
    EventBus.off(GameEvents.PROJECTILE_HIT, this._onProjectileHit, this);
    EventBus.off(GameEvents.GAME_OVER, this._onGameOver, this);
    EventBus.off(GameEvents.BASE_DESTROYED, this._onBaseDestroyed, this);
    EventBus.off(GameEvents.ENEMY_SPAWNED, this._onEnemySpawned, this);
    EventBus.off(GameEvents.ROBOT_DESTROYED, this._onRobotDestroyed, this);
    EventBus.off(GameEvents.PHASE_CHANGED, this._onPhaseChanged, this);
    EventBus.off(GameEvents.LEVEL_UNLOCKED, this._onLevelUnlocked, this);
    EventBus.off(GameEvents.COMPONENT_UNLOCKED, this._onComponentUnlocked, this);
    EventBus.off(GameEvents.GRID_EXPANDED, this._onGridExpanded, this);
  }
}

// Singleton export
export const SoundManager = new SoundManagerClass();
